import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface PageShellProps {
  eyebrow?: string | undefined;
  title: ReactNode;
  lead?: string | undefined;
  back?: ReactNode | undefined;
  actions?: ReactNode | undefined;
  children: ReactNode;
  className?: string | undefined;
  /** Classe extra no <header> (ex.: ajustar padding-top da pagina). */
  headerClassName?: string | undefined;
  /** `compact` = 24px SemiBold (mesmo da ficha do cliente). `sm` = 18px SemiBold (módulos de negócio, sem eyebrow). Default = 2rem/800. */
  titleSize?: 'default' | 'compact' | 'sm';
}

// Esqueleto padrão de página: back, eyebrow, título, lead, ações e conteúdo.
// Todo módulo do Hub usa este shell. Não criar layouts custom.
export function PageShell({
  eyebrow,
  title,
  lead,
  back,
  actions,
  children,
  className,
  headerClassName,
  titleSize = 'default',
}: PageShellProps) {
  return (
    <div className={cn('flex flex-col min-h-full', className)}>
      {back && <div className="px-8 pt-6">{back}</div>}
      <header
        className={cn('px-8 pb-4 flex items-start justify-between gap-4', back ? 'pt-3' : 'pt-8', headerClassName)}
      >
        <div className="flex flex-col gap-1.5 min-w-0">
          {/* DS-01: eyebrow 0.72rem; page-title 2rem / 800 / -0.05em */}
          {eyebrow && (
            <span className="text-[0.72rem] font-medium uppercase tracking-wider text-text-muted">
              {eyebrow}
            </span>
          )}
          {titleSize === 'sm' ? (
            <h1 className="text-[18px] font-semibold leading-[24px] text-text">{title}</h1>
          ) : titleSize === 'compact' ? (
            <h1 className="text-[24px] font-semibold leading-[30px] text-text">{title}</h1>
          ) : (
            <h1
              className="text-text font-extrabold"
              style={{ fontSize: '2rem', letterSpacing: '-0.05em', lineHeight: 1.1 }}
            >
              {title}
            </h1>
          )}
          {lead && <p className="text-sm text-text-2 max-w-2xl">{lead}</p>}
        </div>
        {actions && <div className="flex items-center gap-2 shrink-0">{actions}</div>}
      </header>
      <div className="px-8 pb-8 flex-1">{children}</div>
    </div>
  );
}
